import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ExternalLink, AlertCircle, ArrowLeft, Send, CheckCircle2, AlertTriangle, Timer } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";
import { RejectFeedbackModal, CalendlyPopup, ThankYouPopup } from "../../components/modals/FeedbackModals";
import AcceptInterviewModal from "../../components/modals/AcceptInterviewModal";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import { trackEvent, trackEngagementAction, trackPageView } from "../../lib/analytics";
import { trackRationaleApplicationSubmitted } from "../../lib/analytics";
import { getInterviews, clearInterviewsCache } from "../../lib/interviewsClient";

// Utility function to clean up HTML content and remove CSS styles
const cleanHtmlContent = (content) => {
  if (!content) return "";
  
  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;
  
  // Remove all style attributes
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    element.removeAttribute('style');
  });
  
  // Get the cleaned text content but preserve basic formatting
  let cleanedContent = tempDiv.innerHTML;
  
  // Remove common problematic patterns
  cleanedContent = cleanedContent
    .replace(/style="[^"]*"/gi, '') // Remove any remaining style attributes
    .replace(/--tw-[^;]*;/gi, '') // Remove Tailwind CSS custom properties
    .replace(/--tw-[^:]*:[^;]*;/gi, '') // Remove Tailwind CSS variables
    .replace(/font-family:[^;]*;/gi, '') // Remove font-family declarations
    .replace(/font-size:[^;]*;/gi, '') // Remove font-size declarations
    .replace(/background-color:[^;]*;/gi, '') // Remove background-color declarations
    .replace(/color:[^;]*;/gi, '') // Remove color declarations
    .replace(/margin:[^;]*;/gi, '') // Remove margin declarations
    .replace(/padding:[^;]*;/gi, '') // Remove padding declarations
    .replace(/border:[^;]*;/gi, '') // Remove border declarations
    .replace(/display:[^;]*;/gi, '') // Remove display declarations
    .replace(/position:[^;]*;/gi, '') // Remove position declarations
    .replace(/width:[^;]*;/gi, '') // Remove width declarations
    .replace(/height:[^;]*;/gi, '') // Remove height declarations
    .replace(/text-align:[^;]*;/gi, '') // Remove text-align declarations
    .replace(/line-height:[^;]*;/gi, '') // Remove line-height declarations
    .replace(/vertical-align:[^;]*;/gi, '') // Remove vertical-align declarations
    .replace(/class="[^"]*"/gi, '') // Remove class attributes
    .replace(/<span[^>]*>/gi, '') // Remove span opening tags
    .replace(/<\/span>/gi, '') // Remove span closing tags
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
  
  return cleanedContent;
};

// Apply Confirmation Modal Component
function ApplyConfirmModal({ isOpen, onClose, onConfirm, roleTitle, showRationaleRequired = false }) {
  const [agreementChecked, setAgreementChecked] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-lg p-6 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🚀</span>
          <h2 className="text-xl font-semibold text-gray-800">Confirm Your Application</h2>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          You are applying for the role: <strong className="text-gray-700">{roleTitle}</strong>.
        </p>
        {showRationaleRequired && (
          <p className="text-gray-600 text-sm mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
            ✍️ <strong>Note:</strong> Your rationale has been included with this application.
          </p>
        )}
        
        {/* Interview Rules Checklist */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
          <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">📋</span>
            Interview Guidelines
          </h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Email the startup within 24 hrs of being invited to interview with your availability</span>
            </div>
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Test your tech (e.g. Zoom/Google Meet) in advance</span>
            </div>
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Join the interview at least 5 mins early</span>
            </div>
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Send a thank you email straight after the interview</span>
            </div>
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Complete any case study task before the deadline</span>
            </div>
            <div className="flex items-start space-x-3">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
              <span className="font-medium">Only cancel if absolutely necessary, and with 24+ hrs notice</span>
            </div>
          </div>
        </div>

        {/* Agreement Checkbox */}
        <div className="flex items-start space-x-3 mb-6">
          <input
            type="checkbox"
            id="interview-agreement-role"
            checked={agreementChecked}
            onChange={(e) => setAgreementChecked(e.target.checked)}
            className="mt-1 h-4 w-4 text-[#02B491] focus:ring-[#02B491] border-gray-300 rounded"
          />
          <label htmlFor="interview-agreement-role" className="text-sm text-gray-700 cursor-pointer">
            I agree to follow the interview guidelines above and the startup's hiring process if selected. 
            I confirm I'm genuinely interested and ready to engage.
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!agreementChecked}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              agreementChecked 
                ? 'bg-[#08f2c3] hover:bg-[#06d1a7] cursor-pointer' 
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Confirm & Apply
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function RoleDetails() {
  const navigate = useNavigate();
  const query = useQuery();

  // 1) Identify roleId and if we came from interviews
  const rawRoleId = query.get("id") || query.get("roleId");
  const roleId = rawRoleId;

  const lastVisitedPage = sessionStorage.getItem("lastVisitedPage") || "/roles";
  const fromInterviews = /in_process|interview|interviews/i.test(lastVisitedPage) || query.get("from") === "interviews";
  const fromInterviewRequest = query.get("from") === "interview-request";
  const interviewId = query.get("interviewId");
  const backText = fromInterviews ? "Back to interviews" : "Back to roles";

  // Check if coming from interviews page OR interview request - hide apply buttons in this case
  const hideApplyButtons = fromInterviews || fromInterviewRequest;

  // 2) States
  const [role, setRole] = useState(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("company");
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const videoRef = useRef(null);

  // States for application functionality
  const [appliedRoles, setAppliedRoles] = useState([]);
  const [appliedRolesLoaded, setAppliedRolesLoaded] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyStatus, setApplyStatus] = useState({ message: '', type: '' });
  const [showApplyConfirmPopup, setShowApplyConfirmPopup] = useState(false);
  const [applyNotification, setApplyNotification] = useState({ show: false, message: '', type: '' });
  const [showVideoRequiredModal, setShowVideoRequiredModal] = useState(false);

  // State for rationale input
  const [rationale, setRationale] = useState('');

  // Check if already applied - this needs to be updated when role is loaded
  // Initialize with false and update in useEffect
  const [hasAlreadyApplied, setHasAlreadyApplied] = useState(false);

  // State for tracking roles where candidate is currently interviewing
  const [interviewingRoles, setInterviewingRoles] = useState([]);
  const [interviewingRolesLoaded, setInterviewingRolesLoaded] = useState(false);
  const [isCurrentlyInterviewing, setIsCurrentlyInterviewing] = useState(false);

  // State for tracking Application pending roles
  const [applicationPendingRoles, setApplicationPendingRoles] = useState([]);
  const [applicationPendingRolesLoaded, setApplicationPendingRolesLoaded] = useState(false);

  // State for tracking other interview stages
  const [offerMadeRoles, setOfferMadeRoles] = useState([]);
  const [matchRequestedRoles, setMatchRequestedRoles] = useState([]);
  const [unsuccessfulRoles, setUnsuccessfulRoles] = useState([]);
  const [otherStagesLoaded, setOtherStagesLoaded] = useState(false);

  // State for match request notifications
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [matchRequestNotification, setMatchRequestNotification] = useState({ show: false, message: '', type: '' });

  // States for interview request actions (when coming from interview request)
  const [interviewRequestLoading, setInterviewRequestLoading] = useState(false);
  const [showRejectFeedbackModal, setShowRejectFeedbackModal] = useState(false);
  const [interviewRequestAction, setInterviewRequestAction] = useState(null); // 'accepted' or 'declined'

  // States for Calendly and Thank You popups (matching role_card.js implementation)
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);
  const [interviewCalendlyUrl, setInterviewCalendlyUrl] = useState(null); // Store the Calendly URL from interview data
  
  // State for AcceptInterviewModal
  const [showAcceptInterviewModal, setShowAcceptInterviewModal] = useState(false);
  const [interviewAccepted, setInterviewAccepted] = useState(false);
  const [candidateStream, setCandidateStream] = useState("");
  const [candidateVideoUrl, setCandidateVideoUrl] = useState("");
  const [hasBookedFirstCall, setHasBookedFirstCall] = useState(false);

  // Ensure user is logged in
  const { isAuthenticated, isChecking } = useAuthRedirect();

  // Load applied roles from Airtable
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchAppliedRoles = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        // Fetch candidate data to get "Applied this week drop" field
        const response = await axios.get("/api/candidates/fetchCandidate", {
          headers: { Authorization: `Bearer ${token}` }
        });

        const candidateFields = response.data.candidate.fields;
        
        // Set Applied Roles from "Applied this week drop" field
        const appliedToThisWeek = candidateFields["Applied this week drop"];
        // Make sure it's always an array, even if null
        const appliedRoleIds = Array.isArray(appliedToThisWeek) ? appliedToThisWeek : [];
        console.log("Applied roles from Airtable:", appliedRoleIds.length);
        setAppliedRoles(appliedRoleIds);
        setAppliedRolesLoaded(true);
        
        // Get candidate stream (same logic as other files)
        let streamValue = candidateFields["Do not edit - new Stream (for visibility column)"] || "";
        if (!streamValue) {
          streamValue = candidateFields["Stream (from onboarding form) NEW"] || "";
        }
        setCandidateStream(streamValue);
        const videoUrl = candidateFields["Video intro (new)"] || candidateFields["Raw video intro"] || "";
        setCandidateVideoUrl(videoUrl);
        
        // Get candidate booking status
        const bookedFirstCall = candidateFields["booked first call (platform test)"] || false;
        setHasBookedFirstCall(bookedFirstCall);

        // Supplement with interviews data
        try {
          const interviewsResponse = await axios.get("/api/interviews/fetchInterviews", {
            headers: { Authorization: `Bearer ${token}` }
          });

          const interviewData = interviewsResponse.data.interviews || [];
          const interviewRoleIds = interviewData.map(interview => {
            if (interview.fields && interview.fields.Role) {
              if (Array.isArray(interview.fields.Role)) {
                return interview.fields.Role[0];
              }
              return interview.fields.Role;
            }
            return null;
          }).filter(id => id !== null);

          // Merge with existing applied roles, avoiding duplicates
          setAppliedRoles(prev => {
            const combined = [...prev];
            interviewRoleIds.forEach(id => {
              if (!combined.includes(id)) {
                combined.push(id);
              }
            });
            return combined;
          });
        } catch (interviewErr) {
          console.error("Error fetching interviews:", interviewErr);
        }
      } catch (error) {
        console.error("Error fetching applied roles:", error);
        setAppliedRolesLoaded(true); // Mark as loaded even on error
      }
    };

    fetchAppliedRoles();
  }, [isAuthenticated, isChecking]);

  // Load interviewing roles and application pending roles
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchInterviewingRoles = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        // Reuse shared client-side cache for interviews
        const interviewData = await getInterviews();
        
        // Get interviewing roles
        const interviewingRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Interviewing")
          .map(interview => {
            const roleField = interview.fields["Role"];
            if (Array.isArray(roleField)) {
              return roleField[0];
            }
            return roleField;
          })
          .filter(id => id !== null && id !== undefined);

        // Get Application pending roles
        const applicationPendingRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Application pending")
          .map(interview => {
            const roleField = interview.fields["Role"];
            if (Array.isArray(roleField)) {
              return roleField[0];
            }
            return roleField;
          })
          .filter(id => id !== null && id !== undefined);

        // Get Offer made roles
        const offerMadeRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Offer made")
          .map(interview => {
            const roleField = interview.fields["Role"];
            if (Array.isArray(roleField)) {
              return roleField[0];
            }
            return roleField;
          })
          .filter(id => id !== null && id !== undefined);

        // Get Match requested roles
        const matchRequestedRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Match requested")
          .map(interview => {
            const roleField = interview.fields["Role"];
            if (Array.isArray(roleField)) {
              return roleField[0];
            }
            return roleField;
          })
          .filter(id => id !== null && id !== undefined);

        // Get unsuccessful roles (same stages as in roles_list.js)
        const unsuccessfulStages = [
          "Rejected @ CV",
          "Rejected @ Interview", 
          "Offer rejected",
          "Offer retracted",
          "Startup withdrawn",
          "Candidate withdrawn",
          "Rejected Interview Request",
        ];
        
        const unsuccessfulRoleIds = interviewData
          .filter(interview => unsuccessfulStages.includes(interview.fields["Stage"]))
          .map(interview => {
            const roleField = interview.fields["Role"];
            if (Array.isArray(roleField)) {
              return roleField[0];
            }
            return roleField;
          })
          .filter(id => id !== null && id !== undefined);

        console.log("Interviewing roles:", interviewingRoleIds.length);
        console.log("Application pending roles:", applicationPendingRoleIds.length);
        console.log("Offer made roles:", offerMadeRoleIds.length);
        console.log("Match requested roles:", matchRequestedRoleIds.length);
        console.log("Unsuccessful roles:", unsuccessfulRoleIds.length);
        
        setInterviewingRoles(interviewingRoleIds);
        setInterviewingRolesLoaded(true);
        
        setApplicationPendingRoles(applicationPendingRoleIds);
        setApplicationPendingRolesLoaded(true);
        
        setOfferMadeRoles(offerMadeRoleIds);
        setMatchRequestedRoles(matchRequestedRoleIds);
        setUnsuccessfulRoles(unsuccessfulRoleIds);
        setOtherStagesLoaded(true);
      } catch (error) {
        console.error("Error fetching interviewing roles:", error);
        setInterviewingRolesLoaded(true); // Mark as loaded even on error
        setApplicationPendingRolesLoaded(true);
        setOtherStagesLoaded(true);
      }
    };

    fetchInterviewingRoles();
  }, [isAuthenticated, isChecking]);

  // Check if all required data is loaded to prevent race conditions
  const isDataFullyLoaded = appliedRolesLoaded && applicationPendingRolesLoaded && interviewingRolesLoaded && otherStagesLoaded;

  // Set initial apply status if already applied or interviewing
  useEffect(() => {
    if (role && appliedRolesLoaded && interviewingRolesLoaded && applicationPendingRolesLoaded) {
      const applied = appliedRoles.includes(role.id) || applicationPendingRoles.includes(role.id);
      const interviewing = interviewingRoles.includes(role.id);
      
      setHasAlreadyApplied(applied);
      setIsCurrentlyInterviewing(interviewing);

      if (applied) {
        setApplyStatus({ message: '', type: 'success' });
      } else if (interviewing) {
        setApplyStatus({ message: '', type: 'interviewing' });
      }
    }
  }, [role, appliedRoles, appliedRolesLoaded, interviewingRoles, interviewingRolesLoaded, applicationPendingRoles, applicationPendingRolesLoaded]);

  // Check interview request status when coming from interview request URL
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const checkInterviewRequestStatus = async () => {
      // Only check if we're coming from an interview request and have the interview ID
      if (!fromInterviewRequest || !interviewId) return;
      
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        console.log("Checking interview status for interview ID:", interviewId);

        // Fetch all interviews to find the specific interview by ID
        const interviewData = await getInterviews();
        
        // Find the specific interview by ID
        const specificInterview = interviewData.find(interview => interview.id === interviewId);
        
        if (specificInterview) {
          const currentStage = specificInterview.fields["Stage"];
          console.log("Current interview stage:", currentStage);
          
          // Set the interview request action based on current stage
          if (currentStage === "Interviewing") {
            setInterviewRequestAction('accepted');
          } else if (currentStage === "Rejected Interview Request") {
            setInterviewRequestAction('declined');
          }
          // If still "Match requested", keep showing the active buttons (interviewRequestAction remains null)
        } else {
          console.log("Interview not found with ID:", interviewId);
        }
      } catch (error) {
        console.error("Error checking interview request status:", error);
      }
    };

    checkInterviewRequestStatus();
  }, [fromInterviewRequest, interviewId, isAuthenticated, isChecking]);

  // 4) Main effect to get the role
  useEffect(() => {
    // If no roleId, we can stop loading, there's nothing to fetch
    if (!roleId) {
      setIsLoadingRole(false);
      setError("No role ID provided");
      return;
    }

    if (!isAuthenticated || isChecking) return;

    let isMounted = true;

    const fetchRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setIsLoadingRole(false);
          return;
        }

        // Always use fetchSingleRole to get the role directly by ID
        const { data } = await axios.get("/api/roles/fetchSingleRole", {
          headers: { Authorization: `Bearer ${token}` },
          params: { urlId: roleId },
        });

        if (isMounted) {
          setRole(data.role);
          setIsLoadingRole(false);
          if (!data.role) {
            setError("Role not found");
          } else {
            try {
              // Send role page view with rationale flag and identifiers
              trackPageView('/role', {
                role_id: data.role.id,
                role_title: data.role["Role Title"],
                rationale_required: !!data.role["Rationale required?"],
              });
            } catch (_) {}
          }
        }
      } catch (err) {
        console.error("Error fetching role data:", err);
        if (isMounted) {
          setIsLoadingRole(false);
          setError("Error loading role");
        }
      }
    };

    fetchRole();

    return () => {
      isMounted = false;
    };
  }, [roleId, isAuthenticated, isChecking]);

  // 5) Video playback
  const handlePlay = () => {
    if (currentlyPlaying && currentlyPlaying !== videoRef.current) {
      currentlyPlaying.pause();
    }
    setCurrentlyPlaying(videoRef.current);
    
    // Standalone event for spotlight watching - only fires on user click
    trackEvent("Spotlight Watched", {
      role_id: role?.id,
      role_title: role?.["Role Title"],
      video_source: "dropbox",
      page: "role_details"
    });
  };

  // Track Loom click state per video to enable autoplay and avoid duplicate events
  const loomPlayedSetRef = useRef(new Set());
  const [loomOverlayNonce, setLoomOverlayNonce] = useState(0);

  const renderSpotlightVideo = (spot, index) => {
    const dropboxUrl =
      Array.isArray(spot["URL Dropbox cleaned"]) && spot["URL Dropbox cleaned"].length
        ? spot["URL Dropbox cleaned"][0]
        : spot["URL Dropbox cleaned"];
    const loomUrl =
      Array.isArray(spot["URL Loom cleaned"]) && spot["URL Loom cleaned"].length
        ? spot["URL Loom cleaned"][0]
        : spot["URL Loom cleaned"];

    if (dropboxUrl) {
      return (
        <video
          key={index}
          ref={videoRef}
          src={dropboxUrl}
          className="w-full h-[400px] border object-cover rounded-lg bg-gray-100 mb-4 shadow-sm"
          controls
          onPlay={handlePlay}
          onPlaying={() => {
            try {
              // Only track for page engagement, not standalone
              trackEngagementAction("video_play", { role_id: role?.id, role_title: role?.["Role Title"], source: "dropbox", page: "role_details" });
            } catch (_) {}
          }}
          onTimeUpdate={(e) => {
            try {
              const v = e.currentTarget;
              if (!v || !v.duration || v.duration === Infinity) return;
              const pct = Math.round((v.currentTime / v.duration) * 100);
              const milestones = [25, 50, 75, 100];
              v.__mpMilestones = v.__mpMilestones || new Set();
              milestones.forEach(m => {
                if (pct >= m && !v.__mpMilestones.has(m)) {
                  v.__mpMilestones.add(m);
                  trackEngagementAction("video_progress", { role_id: role?.id, role_title: role?.["Role Title"], percent: m, source: "dropbox", page: "role_details" });
                }
              });
            } catch (_) {}
          }}
        />
      );
    }
    if (loomUrl) {
      const loomKey = `${index}-${loomUrl}`;
      const hasPlayed = loomPlayedSetRef.current.has(loomKey);
      const effectiveLoomSrc = hasPlayed ? (loomUrl.includes('?') ? `${loomUrl}&autoplay=1` : `${loomUrl}?autoplay=1`) : loomUrl;
      return (
        <div className="w-full h-[400px] rounded-lg mb-4 overflow-hidden relative">
          <iframe
            key={index}
            src={effectiveLoomSrc}
            className="w-full h-full object-cover rounded-lg bg-gray-100 shadow-sm"
            title={`Spotlight Video ${index}`}
          />
          {!hasPlayed && (
            <div
              className="absolute inset-0 bg-transparent cursor-pointer z-10"
              onClick={() => {
                try {
                  trackEvent("Spotlight Watched", {
                    role_id: role?.id,
                    role_title: role?.["Role Title"],
                    video_source: "loom",
                    page: "role_details"
                  });
                } catch (_) {}
                try {
                  trackEngagementAction("video_play", { role_id: role?.id, role_title: role?.["Role Title"], source: "loom", page: "role_details" });
                } catch (_) {}
                loomPlayedSetRef.current.add(loomKey);
                setLoomOverlayNonce((n) => n + 1);
              }}
              title="Click to play"
            />
          )}
        </div>
      );
    }
    return null;
  };

  // Apply Now functionality
  const handleApplyNowClick = () => {
    if (isApplying || hasAlreadyApplied || isCurrentlyInterviewing || !role || hideApplyButtons) return;
    
    // Pre-apply guard: Sales candidates must have a video intro
    const streamValue = Array.isArray(candidateStream) ? candidateStream.join("") : String(candidateStream || "");
    const isSales = streamValue.toLowerCase().includes("sales");
    const hasVideo = !!(candidateVideoUrl && String(candidateVideoUrl).trim());
    if (isSales && !hasVideo) {
      setShowVideoRequiredModal(true);
      setApplyNotification({
        show: true,
        message: "Please upload your video intro on your profile before applying.",
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return;
    }

    // Check if rationale is required and not provided
    if (role["Rationale required?"] && !rationale.trim()) {
      try { trackEvent("Quick Apply To Rationale Role", { role_id: role.id, role_title: role["Role Title"], page: "role_details" }); } catch (_) {}
      // Scroll to rationale section
      const rationaleSection = document.getElementById('rationale-section');
      if (rationaleSection) {
        rationaleSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Add red border to textarea
      const textarea = document.getElementById('rationale-textarea');
      if (textarea) {
        textarea.classList.add('border-red-500', 'ring-red-500');
        setTimeout(() => {
          textarea.classList.remove('border-red-500', 'ring-red-500');
        }, 3000);
      }
      
      setApplyNotification({
        show: true,
        message: "Please complete the rationale section before applying.",
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 4000);
      return;
    }
    
    try { trackEvent("Quick Apply Clicked", { role_id: role.id, role_title: role["Role Title"], page: "role_details" }); } catch (_) {}
    setShowApplyConfirmPopup(true);
  };

  const executeApplyNow = async () => {
    setShowApplyConfirmPopup(false);
    if (!roleId || !role) return;

    // Check if rationale is required and provided
    if (role["Rationale required?"] && !rationale.trim()) {
      setApplyNotification({
        show: true,
        message: "Please provide a rationale for this role application.",
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 4000);
      return;
    }

    setIsApplying(true);
    setApplyStatus({ message: '', type: '' });

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      // Check if already applied
      if (appliedRoles.includes(role.id) || applicationPendingRoles.includes(role.id)) {
        setApplyNotification({
          show: true,
          message: `You've already applied to ${role["Role Title"]}!`,
          type: 'info'
        });
        setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
        setIsApplying(false);
        return;
      }

      // Check if currently interviewing
      if (interviewingRoles.includes(role.id)) {
        setApplyNotification({
          show: true,
          message: `You're already interviewing for ${role["Role Title"]}!`,
          type: 'info'
        });
        setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
        setIsApplying(false);
        return;
      }

      // IMPORTANT: Use the actual role.id property, NOT the URL parameter
      // The URL ID (roleId from URL) is used for looking up the role,
      // but for creating an interview, we need the actual Airtable record ID
      console.log("Applying to role with ID:", role.id);
      console.log("Role object:", role);
      
      const data = {
        fields: {
          Role: [role.id], // Use role.id instead of roleId from URL
          "Match type": "Application",
          "Stage": "Application pending",
          ...(rationale.trim() && { "Application Rationale": rationale.trim() }),
        },
        token: token,
      };

      console.log("Sending application data:", data);

      const response = await axios.post("/api/interviews/createInterview", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      });

      console.log("Application response:", response.data);

      // Update local state
      setAppliedRoles(prev => [...prev, role.id]);
      setApplyStatus({ message: 'Applied successfully!', type: 'success' });
      try {
        // Track application submitted only after confirmed success
        trackEvent("Application Submitted", { role_id: role.id, role_title: role["Role Title"], rationale_provided: !!rationale.trim(), page: "role_details" });
        if (role["Rationale required?"] && rationale.trim()) {
          trackRationaleApplicationSubmitted({ role_id: role.id, role_title: role["Role Title"], page: "role_details" });
        }
      } catch (_) {}
      
      // Show notification
      setApplyNotification({
        show: true,
        message: `Successfully applied to ${role["Role Title"]}!`,
        type: 'success'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error("Error applying to role:", error);
      console.error("Error details:", error.response?.data);
      let errorMessage = `Error applying to ${role["Role Title"]}.`;
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setApplyStatus({ message: errorMessage, type: 'error' });
      setApplyNotification({
        show: true,
        message: errorMessage,
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
    } finally {
      setIsApplying(false);
    }
  };

  // Button classes based on state
  const getApplyButtonClasses = () => {
    if (!isDataFullyLoaded) {
      return 'bg-gray-400 text-white cursor-not-allowed';
    } else if (hasAlreadyApplied) {
      return 'bg-green-500 text-white cursor-not-allowed';
    } else if (isCurrentlyInterviewing) {
      return 'bg-green-500 text-white cursor-not-allowed';
    } else if (isApplying) {
      return 'bg-gray-400 text-white cursor-not-allowed';
    } else if (applyStatus.type === 'success') {
      return 'bg-green-500 text-white cursor-not-allowed';
    } else if (applyStatus.type === 'interviewing') {
      return 'bg-green-500 text-white cursor-not-allowed';
    } else if (applyStatus.type === 'error') {
      return 'bg-red-500 text-white';
    } else {
      return 'bg-[#08f2c3] text-black hover:bg-[#06d1a7]';
    }
  };

  // Functions for interview request actions
  const handleAcceptInterviewRequest = async () => {
    if (!interviewId) return;
    
    setInterviewRequestLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      
      console.log("Accepting interview with ID:", interviewId);
      
      // Prepare the data structure to match what the backend expects (same as role_card.js)
      const updatedData = {
        fields: {
          Stage: "Interviewing",
        },
        interviewId: interviewId,
      };

      // If the request is being accepted, also update the candidate-facing stage
      updatedData.fields["Candidate Interview Stage"] = "Interviewing";
      
      console.log("Sending accept request with data:", updatedData);
      
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      console.log("Response from accept:", response.data);
      
      if (response.data && response.data.success) {
        setInterviewRequestAction('accepted');
        setInterviewAccepted(true); // Show success step in modal
        clearInterviewsCache();
        
        // Only show Calendly/Thank you popups if AcceptInterviewModal is not open
        if (!showAcceptInterviewModal) {
          // The Calendly link is in the interview record, not the role record
          // So we need to fetch the interview data to get the Calendly link
          try {
            console.log("Fetching interview data to get Calendly link for interview ID:", interviewId);
            
            const interviewData = await getInterviews({ forceRefresh: true });
            
            // Find the specific interview by ID
            const specificInterview = interviewData.find(interview => interview.id === interviewId);
            
            if (specificInterview) {
              console.log("Found interview record:", specificInterview);
              
              // Get the Calendly link from the interview record (same as role_card.js)
              const calendlyUrlField = specificInterview.fields["HL Calendly Link (if applicable)"];
              let calendlyUrl = null;
              
              console.log("Calendly URL Field from interview:", calendlyUrlField);
              console.log("Calendly URL Field type:", typeof calendlyUrlField);
              
              if (calendlyUrlField) {
                if (typeof calendlyUrlField === 'string') {
                  calendlyUrl = calendlyUrlField;
                  console.log("Found string Calendly URL:", calendlyUrl);
                } else if (Array.isArray(calendlyUrlField) && calendlyUrlField.length > 0) {
                  calendlyUrl = calendlyUrlField[0];
                  console.log("Found array Calendly URL:", calendlyUrl);
                }
              }
              
              console.log("Final Calendly URL:", calendlyUrl);
              
              if (calendlyUrl && typeof calendlyUrl === 'string' && calendlyUrl.trim()) {
                console.log("Showing Calendly popup");
                setInterviewCalendlyUrl(calendlyUrl); // Store the Calendly URL
                setShowCalendlyPopup(true); // Show Calendly popup if link is available
              } else {
                console.log("Showing Thank You popup - no Calendly link found in interview record");
                setShowThankYouPopup(true); // Show thank you popup if no Calendly link
              }
            } else {
              console.log("Interview not found with ID:", interviewId, "showing Thank You popup");
              setShowThankYouPopup(true);
            }
          } catch (interviewFetchError) {
            console.error("Error fetching interview data for Calendly link:", interviewFetchError);
            // Fallback to thank you popup if we can't fetch interview data
            setShowThankYouPopup(true);
          }
        }
      } else {
        throw new Error(response.data?.message || "Accept failed");
      }
    } catch (error) {
      console.error("Error accepting interview request:", error);
      
      // Handle different types of errors (same as role_card.js)
      if (error.response) {
        console.error("Server response:", error.response.data);
        console.error("Failed to accept interview request:", error.response.data?.message || error.response.data?.error || "Server error");
      } else if (error.request) {
        console.error("No response received:", error.request);
        console.error("No response from server. Please check your connection and try again.");
      } else {
        console.error("Failed to accept interview request:", error.message || "Unknown error");
      }
    } finally {
      setInterviewRequestLoading(false);
    }
  };

  const handleDeclineInterviewRequest = () => {
    setShowRejectFeedbackModal(true);
  };

  const handleBookCall = () => {
    setHasBookedFirstCall(true);
    // Could add a notification here if needed
  };

  const handleDeclineWithFeedback = async (feedbackData) => {
    if (!interviewId) return;

    setInterviewRequestLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        setShowRejectFeedbackModal(false);
        return;
      }
      
      console.log("Declining interview with feedback data:", feedbackData);
      console.log("Using interview ID:", interviewId);
      
      // Prepare the data structure to match what the backend expects (same as role_card.js)
      const updatedData = {
        fields: {
          Stage: "Rejected Interview Request",
          "Candidate Interview Stage": "Rejected Match Request", // Also update candidate-facing stage
        },
        interviewId: interviewId,
      };

      // Handle feedback data structure (same as working role_card.js)
      if (feedbackData) {
        updatedData.fields["MR Rejection Feedback"] = feedbackData.reason;
        
        if (feedbackData.reason === "Other" && feedbackData.otherText) {
          updatedData.fields["MR Rejection Feedback (other)"] = feedbackData.otherText;
        }
      }
      
      console.log("Sending request with data:", updatedData);
      
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 
        }
      );
      
      console.log("Response from decline:", response.data);
      
      if (response.data && response.data.success) {
        setInterviewRequestAction('declined');
      } else {
        throw new Error(response.data?.message || "Decline failed");
      }
    } catch (error) {
      console.error("Error declining interview request:", error);
      
      // Handle different types of errors (same as role_card.js)
      if (error.response) {
        console.error("Server response:", error.response.data);
        console.error("Failed to decline interview request:", error.response.data?.message || error.response.data?.error || "Server error");
      } else if (error.request) {
        console.error("No response received:", error.request);
        console.error("No response from server. Please check your connection and try again.");
      } else {
        console.error("Failed to decline interview request:", error.message || "Unknown error");
      }
    } finally {
      setInterviewRequestLoading(false);
      setShowRejectFeedbackModal(false);
    }
  };

  // 6) Determine what to render
  if (isLoadingRole) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-gray-600 animate-pulse">Loading role data...</div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="text-gray-600 mb-4">{error || "No role found."}</div>
        <button
          onClick={() => navigate(lastVisitedPage)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black bg-[#08f2c3] rounded-md hover:bg-[#06d1a7]"
        >
          <ArrowLeft className="w-4 h-4" />
          {backText}
        </button>
      </div>
    );
  }

  // Otherwise, we have a valid role object
  const rawStartupDesc = role["Startup Description (from Startup)"] || "";
  const startupDesc = Array.isArray(rawStartupDesc)
    ? rawStartupDesc.join("\n")
    : rawStartupDesc;

  // Check if there are different types of spotlights
  const companyOnlySpotlights = (role.spotlightDetails || []).filter((s) =>
    s["Type"] === "Company information"
  );
  const roleOnlySpotlights = (role.spotlightDetails || []).filter((s) =>
    s["Type"] === "Role information"
  );
  const companyAndRoleSpotlights = (role.spotlightDetails || []).filter((s) =>
    s["Type"] === "Company and Role"
  );

  // Determine which tabs to show based on spotlight types
  const hasOnlyCompanyAndRole = companyAndRoleSpotlights.length > 0 && 
    companyOnlySpotlights.length === 0 && 
    roleOnlySpotlights.length === 0;

  // Filter spotlights for company and role tabs
  const companySpotlights = !hasOnlyCompanyAndRole ? (role.spotlightDetails || []).filter((s) =>
    ["Company information", "Company and Role"].includes(s["Type"])
  ) : [];
  const roleSpotlights = !hasOnlyCompanyAndRole ? (role.spotlightDetails || []).filter((s) =>
    ["Role information", "Company and Role"].includes(s["Type"])
  ) : [];

  // 7) Render the final role details
  return (
    <div className="bg-gradient-to-b from-white to-gray-50 min-h-screen">
      {/* Apply Confirmation Modal */}
      <ApplyConfirmModal
        isOpen={showApplyConfirmPopup}
        onClose={() => setShowApplyConfirmPopup(false)}
        onConfirm={executeApplyNow}
        roleTitle={role["Role Title"]}
        showRationaleRequired={role && role["Rationale required?"] && rationale.trim()}
      />

      {/* Reject Feedback Modal for Interview Requests */}
      {showRejectFeedbackModal && (
        <RejectFeedbackModal
          isOpen={showRejectFeedbackModal}
          onClose={() => setShowRejectFeedbackModal(false)}
          onSubmit={handleDeclineWithFeedback}
          roleTitle={role["Role Title"]}
        />
      )}

      {/* Calendly Popup for Interview Requests */}
      <CalendlyPopup
        open={showCalendlyPopup}
        onClose={() => setShowCalendlyPopup(false)}
        company={role["Name (from Startup)"]}
        calendlyUrl={interviewCalendlyUrl}
      />

      {/* Thank You Popup for Interview Requests */}
      <ThankYouPopup
        open={showThankYouPopup}
        onClose={() => setShowThankYouPopup(false)}
        company={role["Name (from Startup)"]}
      />

      {/* Accept Interview Modal */}
      <AcceptInterviewModal
        isOpen={showAcceptInterviewModal}
        onClose={() => {
          setShowAcceptInterviewModal(false);
          setInterviewAccepted(false); // Reset for next time
        }}
        onConfirm={() => {
          handleAcceptInterviewRequest();
        }}
        roleTitle={role["Role Title"]}
        companyName={role["Name (from Startup)"]}
        candidateStream={candidateStream}
        interviewAccepted={interviewAccepted}
        hasBookedFirstCall={hasBookedFirstCall}
        onBookCall={handleBookCall}
      />

      {/* Apply Notification */}
      {applyNotification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg 
          ${applyNotification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
           applyNotification.type === 'info' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 
           'bg-red-100 text-red-800 border border-red-200'}`}>
          <div className="flex items-center">
            {applyNotification.type === 'success' && <CheckCircle2 className="w-5 h-5 mr-2" />}
            {applyNotification.type === 'error' && <AlertTriangle className="w-5 h-5 mr-2" />}
            {applyNotification.type === 'info' && <AlertCircle className="w-5 h-5 mr-2" />}
            <span>{applyNotification.message}</span>
          </div>
        </div>
      )}

      {/* Sales Video Required Modal */}
      {showVideoRequiredModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl"
          >
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">🎥</span>
              <h2 className="text-lg font-semibold text-gray-800">Add a video intro to apply</h2>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              Applications require a short video intro on your profile. Please upload your video first, then come back to apply.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowVideoRequiredModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowVideoRequiredModal(false); navigate('/profile'); }}
                className="px-4 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg"
              >
                Go to profile
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* back button */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate(lastVisitedPage)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black bg-[#08f2c3] rounded-md hover:bg-[#06d1a7]"
          >
            <ArrowLeft className="w-4 h-4" />
            {backText}
          </button>

          {/* Apply Button - Top Right */}
          {!hideApplyButtons && !fromInterviewRequest && (
            <button
              onClick={handleApplyNowClick}
              disabled={!isDataFullyLoaded || isApplying || applyStatus.type === 'success' || hasAlreadyApplied || isCurrentlyInterviewing}
              className={`flex items-center justify-center gap-2 py-2 px-6 rounded-lg transition-all duration-200 ease-in-out font-medium ${getApplyButtonClasses()}`}
            >
              <AnimatePresence mode="wait">
                {!isDataFullyLoaded ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </motion.div>
                ) : isApplying ? (
                  <motion.div
                    key="applying"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Applying...</span>
                  </motion.div>
                ) : hasAlreadyApplied || applyStatus.type === 'success' ? (
                  <motion.div
                    key="applied"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" /> 
                    <span>{hasAlreadyApplied ? 'Already Applied' : 'Applied!'}</span>
                  </motion.div>
                ) : isCurrentlyInterviewing || applyStatus.type === 'interviewing' ? (
                  <motion.div
                    key="interviewing"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>Interviewing</span>
                  </motion.div>
                ) : applyStatus.type === 'error' ? (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2"
                  >
                    <AlertTriangle className="w-5 h-5" /> 
                    <span>Error</span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="applyNow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Apply Now</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          )}

          {/* Interview Request Buttons - Top Right */}
          {fromInterviewRequest && interviewId && (
            <div className="flex gap-3">
              {interviewRequestAction === 'accepted' ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Interview Request Accepted</span>
                </div>
              ) : interviewRequestAction === 'declined' ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium">
                  <AlertCircle className="w-5 h-5" />
                  <span>Interview Request Declined</span>
                </div>
              ) : (
                <>
                                  <button
                  onClick={handleDeclineInterviewRequest}
                  disabled={interviewRequestLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                >
                    {interviewRequestLoading ? 'Processing...' : 'Decline'}
                  </button>
                  <button
                    onClick={() => setShowAcceptInterviewModal(true)}
                    disabled={interviewRequestLoading}
                    className="px-6 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {interviewRequestLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Accepting...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Accept Interview</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Status Display when coming from In Process page - Top Right */}
          {fromInterviews && !fromInterviewRequest && isDataFullyLoaded && role && (
            <div className="flex items-center gap-2">
              {offerMadeRoles.includes(role.id) ? (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-purple-500 text-white cursor-not-allowed font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Offer Made</span>
                </button>
              ) : isCurrentlyInterviewing ? (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-green-500 text-white cursor-not-allowed font-medium">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Interviewing</span>
                </button>
              ) : matchRequestedRoles.includes(role.id) ? (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-yellow-500 text-white cursor-not-allowed font-medium">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Match Requested</span>
                </button>
              ) : hasAlreadyApplied || applicationPendingRoles.includes(role.id) ? (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-green-500 text-white cursor-not-allowed font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Already Applied</span>
                </button>
              ) : unsuccessfulRoles.includes(role.id) ? (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-red-500 text-white cursor-not-allowed font-medium">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Unsuccessful</span>
                </button>
              ) : (
                <button className="flex items-center justify-center gap-2 py-2 px-6 rounded-lg bg-gray-400 text-white cursor-not-allowed font-medium">
                  <AlertCircle className="w-5 h-5" />
                  <span>No Application</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Spotlights */}
        {!!role.spotlightDetails?.length && (
          <div className="mt-6 mb-8">
            <div className="flex border-b border-gray-200">
              {hasOnlyCompanyAndRole ? (
                <button
                  className="py-2 px-4 text-sm font-medium border-b-2 border-[#08f2c3] text-[#08f2c3]"
                >
                  Company and Role
                </button>
              ) : (
                <>
                  <button
                    className={`py-2 px-4 text-sm font-medium transition-colors duration-200 ${
                      activeTab === "company"
                        ? "border-b-2 border-[#08f2c3] text-[#08f2c3]"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    onClick={() => setActiveTab("company")}
                  >
                    Company Spotlight
                  </button>
                  <button
                    className={`py-2 px-4 text-sm font-medium transition-colors duration-200 ${
                      activeTab === "role"
                        ? "border-b-2 border-[#08f2c3] text-[#08f2c3]"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    onClick={() => setActiveTab("role")}
                  >
                    Role Spotlight
                  </button>
                </>
              )}
            </div>
            <div className="mt-4">
              {hasOnlyCompanyAndRole ? (
                <div className="space-y-4">
                  {companyAndRoleSpotlights.map((spot, i) => renderSpotlightVideo(spot, i))}
                </div>
              ) : (
                <>
                  {activeTab === "company" && companySpotlights.length > 0 && (
                    <div className="space-y-4">
                      {companySpotlights.map((spot, i) => renderSpotlightVideo(spot, i))}
                    </div>
                  )}
                  {activeTab === "company" && companySpotlights.length === 0 && (
                    <p className="text-sm text-gray-500 py-3">No company spotlight available.</p>
                  )}

                  {activeTab === "role" && roleSpotlights.length > 0 && (
                    <div className="space-y-4">
                      {roleSpotlights.map((spot, i) => renderSpotlightVideo(spot, i))}
                    </div>
                  )}
                  {activeTab === "role" && roleSpotlights.length === 0 && (
                    <p className="text-sm text-gray-500 py-3">No role spotlight available.</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Role Title / Company */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold tracking-tight">
            {role["Role Title"] || "Untitled Role"}
          </h1>
          <p className="text-lg text-gray-600 mt-1">
            {role["Name (from Startup)"] || "Unnamed Company"}
          </p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-6">
          {role["Compensation"] && (
            <span className="inline-flex mr-2 items-center gap-x-1 rounded-md bg-purple-100 px-4 py-1 text-sm font-medium text-purple-700 ring-1 ring-inset ring-purple-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              {role["Compensation"]}
            </span>
          )}
          {role["Team Size (from Startup)"] && (
            <span className="inline-flex mr-2 items-center gap-x-1 rounded-md bg-green-100 px-4 py-1 text-sm font-medium text-green-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                />
              </svg>
              {role["Team Size (from Startup)"]} in the team
            </span>
          )}
          {role["Funding Stage (from Startup)"] && (
            <span className="inline-flex mr-2 items-center gap-x-1 rounded-md bg-red-100 px-4 py-1 text-sm font-medium text-red-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
                />
              </svg>
              {role["Funding Stage (from Startup)"]}
            </span>
          )}
          {role["Location (from Startup)"] && (
            <span className="inline-flex mr-2 items-center gap-x-1 rounded-md bg-blue-100 px-4 py-1 text-sm font-medium text-blue-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                />
              </svg>
              {role["Location (from Startup)"]}
            </span>
          )}
          {role["Working style category"] && (
            <span className="inline-flex items-center gap-x-1 rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-200">
              <Timer className="w-4 h-4" />
              {role["Working style category"]}
            </span>
          )}
        </div>

        <hr className="my-6 border-gray-200" />

        {/* Company Description */}
        {startupDesc.trim() && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-3">Company Description</h2>
            <div className="text-base text-gray-700 leading-relaxed">
              <ReactMarkdown>{startupDesc}</ReactMarkdown>
            </div>
            {role["Website (from Startup)"] && (
              <a
                href={role["Website (from Startup)"]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mt-3 text-base text-[#08f2c3] font-medium hover:text-[#06d1a7]"
              >
                Visit Website
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            )}
          </div>
        )}

        {/* Key Skills */}
        {role["Specific Experience"] && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-3">Key Skills</h2>
            {/* Check if content contains HTML tags */}
            {/<[^>]+>/.test(role["Specific Experience"]) ? (
              <div 
                className="text-base text-gray-700 leading-relaxed rich-text-content"
                dangerouslySetInnerHTML={{ __html: role["Specific Experience"] }}
              />
            ) : (
              <p className="text-base text-gray-700 whitespace-pre-line leading-relaxed">
                {role["Specific Experience"]}
              </p>
            )}
          </div>
        )}

        {/* Interview Process */}
        {role["Interview Process"] && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-3">Interview Process</h2>
            <div className="text-base text-gray-700 leading-relaxed rich-text-content">
              {/* Check if content contains HTML tags */}
              {/<[^>]+>/.test(role["Interview Process"]) ? (
                <div 
                  className="interview-process-content"
                  dangerouslySetInnerHTML={{ __html: cleanHtmlContent(role["Interview Process"]) }}
                />
              ) : (
                <ReactMarkdown>{role["Interview Process"]}</ReactMarkdown>
              )}
            </div>
          </div>
        )}

        {/* WORKING STYLE */}
        {role["Working Style"] && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-3">Team Working Style</h2>
            <div className="text-base text-gray-700 leading-relaxed rich-text-content">
                <ReactMarkdown>{role["Working Style"]}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* HIRING LEAD */}
        {role["Hiring Lead Name"] && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-3">Hiring Lead</h2>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {role["Hiring Lead Name"]}
                  </h3>
                  {role["Hiring Lead Email Address"] && (
                    <p className="text-sm text-gray-600">
                      {role["Hiring Lead Email Address"]}
                    </p>
                  )}
                </div>
                {role["Hiring Lead Linkedin"] && (
                  <a
                    href={role["Hiring Lead Linkedin"].startsWith('http') ? role["Hiring Lead Linkedin"] : `https://linkedin.com/in/${role["Hiring Lead Linkedin"]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white text-sm font-medium rounded-lg hover:bg-[#005885] transition-colors duration-200"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn profile
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Job Description PDF */}
        {role["Job Description PDF"] ? (
          <div className="mb-6">
            <a
              href={role["Job Description PDF"]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-black bg-[#08f2c3] rounded-md hover:bg-[#06d1a7]"
            >
              View full job description
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>
        ) : (
          <div className="flex items-center mb-6 p-3 bg-yellow-50 rounded-md">
            <AlertCircle className="w-4 h-4 mr-2 text-yellow-500" />
            <span className="text-sm text-yellow-700 font-medium">
              Job description not available
            </span>
          </div>
        )}

        {/* Rationale Input Box - Only show if rationale required and not already applied */}
        {!hideApplyButtons && !fromInterviewRequest && role && role["Rationale required?"] && !hasAlreadyApplied && !isCurrentlyInterviewing && (
          <div id="rationale-section" className="mt-8 mb-6 border border-gray-200 rounded-lg p-6 bg-gray-50">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Brief Rationale Required
              </h3>
              <div className="text-sm text-gray-700 leading-relaxed mb-4">
                <p className="mb-2">
                  Whilst you can apply for most of our roles in just two-clicks, for this role, <strong>{role["Name (from Startup)"]}</strong> has requested that you write a brief rationale of up to 100 words to explain why you'd be a great fit.
                </p>
                <p className="font-medium text-gray-800 mb-1">Things you MUST do:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li><strong>Spell-check!</strong> It doesn't make a great first impression if you mis-spell the company name or make a tonne of grammatical errors.</li>
                  <li><strong>Identify the top attributes they are after.</strong> Re-watch the founder video pitch and re-read the JD to really understand what they are looking for and explain why you are a good fit.</li>
                  <li><strong>Personalise it to the startup.</strong> For obvious reasons!</li>
                </ul>
              </div>
              <div className="relative">
                <textarea
                  id="rationale-textarea"
                  value={rationale}
                  onChange={(e) => {
                    setRationale(e.target.value);
                    // Auto-resize the textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                  }}
                  placeholder="Write your brief rationale here (up to 100 words)..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#08f2c3]/20 focus:border-[#08f2c3] transition-all text-sm"
                  rows={3}
                  maxLength={600} // Approximately 100 words
                  style={{ minHeight: '80px', maxHeight: '200px' }}
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                  {rationale.split(' ').filter(word => word.length > 0).length}/100 words
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Apply Now Button - Bottom (Large) */}
        {!hideApplyButtons && !fromInterviewRequest && (
          <div className="mt-8 mb-6">
            <button
              onClick={handleApplyNowClick}
              disabled={!isDataFullyLoaded || isApplying || applyStatus.type === 'success' || hasAlreadyApplied || isCurrentlyInterviewing}
              className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg transition-all duration-200 ease-in-out text-lg font-medium ${getApplyButtonClasses()}`}
            >
              <AnimatePresence mode="wait">
                {!isDataFullyLoaded ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </motion.div>
                ) : isApplying ? (
                  <motion.div
                    key="applying"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Applying...</span>
                  </motion.div>
                ) : hasAlreadyApplied || applyStatus.type === 'success' ? (
                  <motion.div
                    key="applied"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" /> 
                    <span>{hasAlreadyApplied ? 'Already Applied' : 'Applied!'}</span>
                  </motion.div>
                ) : isCurrentlyInterviewing || applyStatus.type === 'interviewing' ? (
                  <motion.div
                    key="interviewing"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>Interviewing</span>
                  </motion.div>
                ) : applyStatus.type === 'error' ? (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2"
                  >
                    <AlertTriangle className="w-5 h-5" /> 
                    <span>Error</span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="applyNow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>Apply Now</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        )}

        {/* Interview Request Buttons - Bottom (Large) */}
        {fromInterviewRequest && interviewId && (
          <div className="mt-8 mb-6">
            {interviewRequestAction === 'accepted' ? (
              <div className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-green-100 text-green-800 rounded-lg font-medium text-lg">
                <CheckCircle2 className="w-6 h-6" />
                <span>Interview Request Accepted - Check Your Email</span>
              </div>
            ) : interviewRequestAction === 'declined' ? (
              <div className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gray-100 text-gray-700 rounded-lg font-medium text-lg">
                <AlertCircle className="w-6 h-6" />
                <span>Interview Request Declined</span>
              </div>
            ) : (
              <div className="flex gap-4">
                <button
                  onClick={handleDeclineInterviewRequest}
                  disabled={interviewRequestLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-6 text-lg font-medium text-white bg-red-500 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <AlertTriangle className="w-5 h-5" />
                  <span>{interviewRequestLoading ? 'Processing...' : 'Decline Interview'}</span>
                </button>
                <button
                  onClick={() => setShowAcceptInterviewModal(true)}
                  disabled={interviewRequestLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-6 text-lg font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-colors disabled:opacity-50"
                >
                  {interviewRequestLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Accept Interview Request</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
       {/* Rich Text Content Styling */}
       <style jsx>{`
        .rich-text-content h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .rich-text-content h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .rich-text-content h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0.5rem 0 0.25rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .rich-text-content p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .rich-text-content ul, .rich-text-content ol {
          margin: 0.75rem 0;
          padding-left: 1.5rem;
          line-height: 1.6;
        }
        .rich-text-content li {
          margin: 0.25rem 0;
        }
        .rich-text-content strong {
          font-weight: 600;
          color: #1f2937;
        }
        .rich-text-content em {
          font-style: italic;
        }
        .rich-text-content ul {
          list-style-type: disc;
        }
        .rich-text-content ol {
          list-style-type: decimal;
        }
        .rich-text-content li::marker {
          color: #6b7280;
        }
        .interview-process-content h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .interview-process-content h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .interview-process-content h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0.5rem 0 0.25rem 0;
          line-height: 1.3;
          color: #1f2937;
        }
        .interview-process-content p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .interview-process-content ul, .interview-process-content ol {
          margin: 0.75rem 0;
          padding-left: 1.5rem;
          line-height: 1.6;
        }
        .interview-process-content li {
          margin: 0.25rem 0;
        }
        .interview-process-content strong {
          font-weight: 600;
          color: #1f2937;
        }
        .interview-process-content em {
          font-style: italic;
        }
        .interview-process-content ul {
          list-style-type: disc;
        }
        .interview-process-content ol {
          list-style-type: decimal;
        }
        .interview-process-content li::marker {
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}

export default RoleDetails;
